import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { animate, motion, useInView } from "framer-motion";
import "./About.css";
import ThreeBackground from "../../components/common/ThreeBackground";
import Icon from "../../components/common/Icon";
import { timeline, missionVisionValues, aboutStats, team } from "../../Data/siteContent";

import ahsan from "../../assets/Team/Ahsan.jpg";
import fahad from "../../assets/Team/Fahad.jpg";
import saad from "../../assets/Team/Saad.jpg";
import fakhar from "../../assets/Team/WebExpert.jpg";
import ammar from "../../assets/Team/Ammar.jpg";
import arzoo from "../../assets/Team/arzoo.jpg";
import { useSeo, SEO } from "../../config/seo";

const ease = [0.21, 0.8, 0.25, 1];
const TEAM_IMG = {
  "Ahsan Javed": ahsan,
  "Fahad Razzaq": fahad,
  "Saad Ali": saad,
  "Fakhar Alam": fakhar,
  "Ammar Javid": ammar,
  "Arzoo Niaz": arzoo,
};

const STRATEGY = [
  { n: "1", icon: "spark", title: "Discovery", body: "Understand the real problem." },
  { n: "2", icon: "flask", title: "Blueprint", body: "Design the solution architecture." },
  { n: "3", icon: "gears", title: "Build", body: "Engineer with speed and precision." },
  { n: "4", icon: "rocket", title: "Launch", body: "Deploy, test, iterate." },
  { n: "5", icon: "chart", title: "Scale", body: "Grow with the client." },
];

const WHY = [
  { icon: "shield", title: "No Hidden Costs", body: "Transparent, scoped pricing from day one — no surprises." },
  { icon: "team", title: "Expert Team", body: "Engineers, designers and AI specialists under one roof." },
  { icon: "clock", title: "24/7 Support", body: "Reliable help whenever you need it, day or night." },
  { icon: "brain", title: "Full-Stack Capability", body: "From AI models to hardware — we build the whole stack." },
];

const Reveal = ({ children, delay = 0, className }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.25 }} transition={{ duration: 0.65, delay, ease }}>
    {children}
  </motion.div>
);

function Counter({ to, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return undefined;
    const controls = animate(0, to, { duration: 1.6, ease: "easeOut", onUpdate: (v) => setVal(Math.floor(v)) });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

export default function About() {
  useSeo(SEO.about);
  return (
    <main className="cv-home cv-about">
      {/* A. Hero */}
      <header className="cv-about-hero">
        <ThreeBackground variant="orbs" />
        <div className="cv-about-hero__inner">
          <motion.span className="cv-eyebrow" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
            <span className="dot" /> About CafeVista
          </motion.span>
          <motion.h1 className="cv-about-hero__title" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.08, ease }}>
            The team that builds the{" "}
            <span className="cv-gradient-text">systems businesses run on</span>
          </motion.h1>
          <motion.p className="cv-about-hero__sub" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.16, ease }}>
            We started in 2018 doing software for other people. Eight years and 150+ projects
            later, the problems clients kept bringing us turned into products of our own &mdash; an
            ERP platform, analytics over SAP, and an assistant that answers off live business data.
          </motion.p>
          <motion.div className="cv-about-hero__stats" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }}>
            {[["100+", "Clients"], ["150+", "Projects"], ["8", "Years"], ["4", "Products live"]].map(([v, l]) => (
              <div key={l}><strong>{v}</strong><span>{l}</span></div>
            ))}
          </motion.div>
        </div>
      </header>

      {/* B. Our Story — timeline */}
      <section className="cv-section cv-story">
        <div className="cv-heading cv-heading--center">
          <Reveal><span className="cv-eyebrow"><span className="dot" /> Our Story</span></Reveal>
          <Reveal delay={0.06}><h2 className="cv-h2">From one consultancy to a <span className="cv-gradient-text">company with its own products</span></h2></Reveal>
        </div>
        <div className="cv-timeline">
          <span className="cv-timeline__line" aria-hidden="true" />
          {timeline.map((m, i) => (
            <motion.div
              key={m.year}
              className={`cv-timeline__item ${i % 2 === 0 ? "is-left" : "is-right"}`}
              initial={{ opacity: 0, x: i % 2 === 0 ? -40 : 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6, ease }}
            >
              <span className="cv-timeline__node" />
              <div className="cv-timeline__card cv-glass">
                <span className="cv-timeline__year">{m.year}</span>
                <h3>{m.title}</h3>
                <p>{m.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* C. Mission / Vision / Values */}
      <section className="cv-section cv-mvv">
        <div className="cv-mvv__grid">
          {missionVisionValues.map((c, i) => (
            <motion.div key={c.key} className="cv-mvv__card cv-glass cv-glass-hover" initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.55, delay: i * 0.08, ease }}>
              <span className="cv-mvv__ic"><Icon name={c.icon} size={24} /></span>
              <h3>{c.key}</h3>
              <p>{c.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* D. Stats counter strip */}
      <section className="cv-section cv-section--tight">
        <div className="cv-about-stats cv-glass">
          <div className="cv-stats__glow" aria-hidden="true" />
          {aboutStats.map((s) => (
            <div key={s.label} className="cv-about-stats__item">
              <span className="cv-about-stats__ic"><Icon name={s.icon} size={20} /></span>
              <span className="cv-about-stats__val"><Counter to={s.value} suffix={s.suffix} /></span>
              <span className="cv-about-stats__label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* E. The Team */}
      <section className="cv-section cv-team">
        <div className="cv-heading cv-heading--center">
          <Reveal><span className="cv-eyebrow"><span className="dot" /> The Builders</span></Reveal>
          <Reveal delay={0.06}><h2 className="cv-h2">The people who <span className="cv-gradient-text">ship the work</span></h2></Reveal>
        </div>
        <div className="cv-team__grid">
          {team.map((m, i) => (
            <motion.div
              key={m.name}
              className="cv-team__card cv-glass"
              initial={{ opacity: 0, rotateY: -55, y: 20 }}
              whileInView={{ opacity: 1, rotateY: 0, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease }}
            >
              <div className="cv-team__photo">
                <img src={TEAM_IMG[m.name]} alt={m.name} loading="lazy" />
              </div>
              <h3>{m.name}</h3>
              <p>{m.role}</p>
              <a href="https://www.linkedin.com/company/cafevistaai/" target="_blank" rel="noopener noreferrer" aria-label={`${m.name} on LinkedIn`} className="cv-team__social">
                <Icon name="linkedin" size={17} />
              </a>
            </motion.div>
          ))}
        </div>
      </section>

      {/* F. Strategy */}
      <section className="cv-section cv-recipe">
        <div className="cv-heading cv-heading--center">
          <Reveal><span className="cv-eyebrow"><span className="dot" /> How We Work</span></Reveal>
          <Reveal delay={0.06}><h2 className="cv-h2">How an engagement <span className="cv-coffee-text">actually runs</span></h2></Reveal>
        </div>
        <div className="cv-recipe__row">
          {STRATEGY.map((s, i) => (
            <motion.div key={s.n} className="cv-recipe__step" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.5, delay: i * 0.1, ease }}>
              <div className="cv-recipe__card cv-glass">
                <span className="cv-recipe__num">{s.n}</span>
                <span className="cv-recipe__ic"><Icon name={s.icon} size={22} /></span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
              {i < STRATEGY.length - 1 && <Icon name="arrow" size={20} className="cv-recipe__arrow" />}
            </motion.div>
          ))}
        </div>
      </section>

      {/* G. Why choose us */}
      <section className="cv-section cv-section--tight">
        <div className="cv-about-why">
          {WHY.map((w, i) => (
            <motion.div key={w.title} className="cv-about-why__card cv-glass cv-glass-hover" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5, delay: i * 0.07, ease }}>
              <span className="cv-about-why__ic"><Icon name={w.icon} size={22} /></span>
              <h3>{w.title}</h3>
              <p>{w.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* H. Group connection CTA */}
      <section className="cv-section cv-section--tight">
        <motion.div className="cv-about-cta cv-glass" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6, ease }}>
          <div className="cv-prod-cta__glow" aria-hidden="true" />
          <h2>CafeVista is the software company inside <span className="cv-gradient-text">Cafevista Pvt Ltd</span></h2>
          <p>A software studio that ships client work and its own products, backed by an industrial partner that keeps us honest about how factories really run.</p>
          <Link to="/products" className="cv-btn">See what we've built <Icon name="arrow" size={18} className="arrow" /></Link>
        </motion.div>
      </section>
    </main>
  );
}
